import {
  Card,
  Header,
  IconExternalLink,
  InfoBadge,
  InfoBadgeTypes,
  Space,
  SubText,
  TertiaryButton,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { SanitizedHtml } from '@webapp/platform/discover-more/components/SanitizedHtml'
import { CHANGELOG_DATE_FORMAT } from '@webapp/platform/discover-more/constants'
import { type ChangelogRelease } from '@webapp/platform/discover-more/entities/ChangelogRelease'
import messages from './messages'
import { BodyStyled } from './styles/BodyStyled'
import { ChangelogReleaseStyled } from './styles/ChangelogReleaseStyled'
import { HeroImageStyled } from './styles/HeroImageStyled'

const NEW_CHANGE_TYPE_SLUG = 'new'

export interface IChangelogReleaseListProps {
  releases: ChangelogRelease[]
  /** Fired before the browser follows the release's link to the public changelog. */
  onReleaseClick: (release: ChangelogRelease) => void
}

export const ChangelogReleaseList = ({
  releases,
  onReleaseClick,
}: IChangelogReleaseListProps) => {
  const intl = useProcurifyIntl()

  return (
    <Space size='medium'>
      {releases.map((release) => (
        <Card key={release.id}>
          <Card.Content>
            <ChangelogReleaseStyled>
              {release.imageUrl && (
                <HeroImageStyled
                  src={release.imageUrl}
                  alt={release.imageAlt ?? release.title}
                />
              )}

              <Space>
                <Space direction='horizontal' size='small' align='center'>
                  {release.changeType && (
                    <InfoBadge
                      type={
                        release.changeType.slug === NEW_CHANGE_TYPE_SLUG
                          ? InfoBadgeTypes.HIGH_PRIORITY
                          : InfoBadgeTypes.NEUTRAL
                      }
                      text={release.changeType.name}
                      size='small'
                    />
                  )}

                  {release.productCategory && (
                    <InfoBadge
                      type={InfoBadgeTypes.NEUTRAL}
                      text={release.productCategory.name}
                      size='small'
                    />
                  )}

                  <SubText noWrap autoMargin={false}>
                    {intl.formatDate(
                      release.publishedAt,
                      CHANGELOG_DATE_FORMAT
                    )}
                  </SubText>
                </Space>

                <Header as='h2'>{release.title}</Header>

                <BodyStyled>
                  <SanitizedHtml html={release.body} />
                </BodyStyled>

                <a
                  href={release.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={() => onReleaseClick(release)}
                >
                  <TertiaryButton
                    iconComponent={<IconExternalLink />}
                    type='button'
                  >
                    {intl.formatMessage(messages.readMoreLink)}
                  </TertiaryButton>
                </a>
              </Space>
            </ChangelogReleaseStyled>
          </Card.Content>
        </Card>
      ))}
    </Space>
  )
}

ChangelogReleaseList.displayName = 'ChangelogReleaseList'
